import _flow from 'lodash/flow'
import _orderBy from 'lodash/orderBy'
import _remove from 'lodash/remove'
import _sortBy from 'lodash/sortBy'
import latinize from 'latinize'
import useMemoizePromise from 'frontcore/utils/promises/memoize-promise'
import { createFilterOption } from 'frontend/helpers/filterSelectorUtils'
import {
  deleteRole,
  getPeople,
  getRoles,
  patchRole,
  saveRole } from 'frontend/communications/apiClient'
import { PROMISE_STATUS } from 'frontcore/constants/promises'
import { sortAlphabetically } from 'frontcore/utils/helpers/formatters'

const stateObj = {
  observersPermissions: [],
  memoizedObservers: useMemoizePromise(),
  people: [],
  peoplePromise: null,
  peopleLoadingStatus: PROMISE_STATUS.notStarted,
  observerPermissionLoadingStatus: PROMISE_STATUS.notStarted,
  memoizedRoles: useMemoizePromise(),
  roles: [],
  rolesLoadingStatus: PROMISE_STATUS.notStarted,
}

const moduleGetters = {
  getActivePeople (state, getters) {
    return _sortBy(getters.getPeople, person => latinize(person.name.toLowerCase())).filter(person => {
      return person.active
    })
  },
  getActivePeopleAndException: (state, getters) => (exception) => {
    const exceptionArray = Array.isArray(exception) ? exception : [exception]
    return _sortBy(getters.getPeople, person => latinize(person.name.toLowerCase())).filter(person => {
      return person.active || exceptionArray.includes(person.id)
    })
  },
  getCollaboratorsOf: (state, getters) => (profile) => {
    return getters.getPeople.filter(person => profile.children.includes(person.id))
  },
  allPeople: (_, getters) => {
    return getters.getPeople
  },
  getPeople (state) {
    if (!state.peoplePromise) {
      console.error("Fetch people first:\n", (new Error()).stack)
    }
    return _sortBy(state.people, person => latinize(person.name.toLowerCase()))
  },
  peopleAreLoaded (state) {
    return state.peopleLoadingStatus === PROMISE_STATUS.complete
  },
  getPeopleByIds: (state, getters) => (ids) => {
    return getters.getPeople.filter(person => ids.some(id => id === person.id))
  },
  getPeopleNamesByIds: (state, getters) => (ids) => {
    return _flow([
      () => getters.getPeopleByIds(ids),
      items => _orderBy(items, 'name'),
      items => items.map(item => item.name),
    ])()
  },
  getOperators (state, getters) {
    return getters.getActivePeople.filter(person => person.is_operator)
  },
  getNotOperators (state, getters) {
    return getters.getActivePeople.filter(person => !person.is_operator )
  },
  getPersonById: (state, getters) => (id) => {
    return getters.getPeople.find(person => person.id === id)
  },
  getRoles (state) {
    return state.roles
  },
  getRoleById: (state, getters) => (id) => {
    return getters.getRoles.find(role => role.id === id)
  },
  rolesAreLoaded (state) {
    return state.rolesLoadingStatus === PROMISE_STATUS.complete
  },
  getObserversPermissions (state) {
    return state.observersPermissions
  },
  getRolesFilterOptions (state, getters) {
    return getters.getRoles.map(role => {
      return createFilterOption({
        text: role.localized_name,
        value: role.id,
      })
    }).sort(sortAlphabetically('text'))
  },
}

const mutations = {
  setPeople (state, value) {
    state.people = value
  },
  setPeopleLoadingStatus (state, status) {
    state.peopleLoadingStatus = status
  },
  setObserverPermissionLoadingStatus (state, status) {
    state.observerPermissionLoadingStatus = status
  },
  updatePerson (state, value) {
    state.memoizedObservers.reset()
    const personIndex = state.people.findIndex(person => person.id === value.id)
    if (personIndex !== -1) {
      state.people.splice(personIndex, 1, value)
    }
  },
  setTeam (state, { id, members }) {
    const oldPeopleOnTeam = state.people
      .filter(person => person.teams_as_member.some(teamId => teamId === id))
    const peopleOnTeam = state.people
      .filter(person => members.some(memberId => memberId === person.id))
    const people = oldPeopleOnTeam.concat(peopleOnTeam)

    people.forEach(person => {
      if (members.some(memberId => memberId === person.id)) {
        const personTeams = person.teams_as_member.slice()
        personTeams.push(id)
        person.teams_as_member = Array.from(new Set(personTeams))
      } else {
        _remove(person.teams_as_member, teamId => teamId === id)
      }
    })
  },
  deleteTeam (state, teamId) {
    const people = state.people
      .filter(person => person.teams_as_member.some(id => id === teamId))
    people.forEach(person => {
      person.teams_as_member = person.teams_as_member
        .filter(id => id !== teamId)
    })
  },
  setRoles (state, roles) {
    state.roles = roles
  },
  createRole (state, role) {
    state.roles.push(role)
    state.memoizedRoles.reset()
  },
  updateRole (state, role) {
    const index = state.roles.findIndex(d => d.id === role.id)
    const auxRoles = { ...state.roles[index], ...role }
    state.roles.splice(index, 1, auxRoles)
    state.memoizedRoles.reset()
  },
  removeRole (state, id) {
    state.roles = state.roles.filter(role => role.id !== id)
    state.memoizedRoles.reset()
  },
  setObserversPermissions (state, observers) {
    state.observersPermissions = observers
  },
  setRolesLoadingStatus (state, status) {
    state.rolesLoadingStatus = status
  },

}

const actions = {
  fetchPeople ({ commit, state }, force=false) {
    if (state.peoplePromise == null || force) {
      state.peoplePromise = getPeople({ format: 'table' }, false)
      commit('setPeopleLoadingStatus', PROMISE_STATUS.pending)
    }
    return state.peoplePromise.then((people) => {
      (force || state.people.length === 0) ? commit('setPeople', people) : Promise.resolve()
    })
      .catch(e => {
        state.peoplePromise = null
        throw e
      })
      .finally(() => {
        commit('setPeopleLoadingStatus', PROMISE_STATUS.complete)
      })
  },
  async fetchObserversPermissions ({ commit, state }, { source, force = false }) {
    const { memoize, reset } = state.memoizedObservers
    if (force) {
      reset()
      commit('setObserverPermissionLoadingStatus', PROMISE_STATUS.notStarted)
    }
    try {
      const observers = await memoize(getPeople, {
        format: 'permissions',
        source: source,
      })
      commit('setObserversPermissions', observers)
      commit('setObserverPermissionLoadingStatus', PROMISE_STATUS.complete)
      return state.observersPermissions
    } catch (error) {
      reset()
      commit('setObserverPermissionLoadingStatus', PROMISE_STATUS.notStarted)
      throw error
    }
  },
  async fetchOperatorPermissions () {
    return getPeople({
      'format': 'permissions',
      'operator': true,
      'active': true,
    }, false)
  },
  async fetchPeopleStandardPermissions () {
    return getPeople({
      'format': 'permissions',
      'standard_supervisor': true,
    }, false)
  },
  async createRole ({ commit }, payload) {
    const role = await saveRole(payload)
    commit('createRole', role)
    return role
  },
  async fetchRoles ({ commit, state }, force = false) {
    const { memoize, reset } = state.memoizedRoles
    if (force) {
      reset()
      commit('setRolesLoadingStatus', PROMISE_STATUS.notStarted)
    }
    try {
      const roles = await memoize(getRoles)
      commit('setRoles', roles)
      commit('setRolesLoadingStatus', PROMISE_STATUS.complete)
    } catch (error) {
      reset()
      commit('setRolesLoadingStatus', PROMISE_STATUS.notStarted)
      throw error
    }
  },
  async updateRole ({ commit }, payload) {
    const id = payload.id
    delete payload.id
    const role = await patchRole(id, payload)
    commit('updateRole', role)
    return role
  },
  async removeRole ({ commit }, id) {
    const role = await deleteRole(id)
    commit('removeRole', id)
    return role
  },
}

export default {
  state: stateObj,
  getters: moduleGetters,
  mutations,
  actions,
}
